/**
 * useNative: exposes platform-aware capabilities.
 * On web, native methods no-op or return browser fallbacks.
 * On iOS/Android (when wrapped in Capacitor), calls the real native plugin.
 */
import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'web' | 'ios' | 'android'

/* ---------- Push Notifications ---------- */
export async function initPushNotifications(onRegistered, onNotification) {
  if (!isNative) return { ok: false, reason: 'web' };
  const { PushNotifications } = await import('@capacitor/push-notifications');
  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== 'granted') return { ok: false, reason: 'permission_denied' };
  await PushNotifications.register();
  PushNotifications.addListener('registration', (token) => {
    onRegistered?.(token.value);
  });
  PushNotifications.addListener('pushNotificationReceived', (n) => {
    onNotification?.(n);
  });
  return { ok: true };
}

/* ---------- Camera / Photo Picker ---------- */
export async function takePhoto(options = {}) {
  const { CameraSource, CameraResultType, Camera } = await import('@capacitor/camera');
  if (!isNative) {
    // Web fallback: prompt user via native file input
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment';
      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) return resolve(null);
        const reader = new FileReader();
        reader.onload = () => resolve({ dataUrl: reader.result, format: file.type.split('/')[1] });
        reader.readAsDataURL(file);
      };
      input.click();
    });
  }
  const image = await Camera.getPhoto({
    quality: 80,
    allowEditing: false,
    resultType: CameraResultType.DataUrl,
    source: options.source === 'gallery' ? CameraSource.Photos : CameraSource.Camera,
    saveToGallery: false,
  });
  return { dataUrl: image.dataUrl, format: image.format };
}

/* ---------- Geolocation ---------- */
export async function getCurrentPosition() {
  if (!isNative && !navigator.geolocation) return null;
  if (!isNative) {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => resolve(null),
        { enableHighAccuracy: true, timeout: 8000 }
      );
    });
  }
  const { Geolocation } = await import('@capacitor/geolocation');
  const perm = await Geolocation.requestPermissions();
  if (perm.location !== 'granted') return null;
  const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 8000 });
  return { lat: pos.coords.latitude, lng: pos.coords.longitude };
}

/* ---------- Biometric Auth ---------- */
export async function authenticateBiometric(reason = 'Confirm your identity') {
  if (!isNative) return { available: false, ok: false, reason: 'web' };
  const { BiometricAuth } = await import('@aparajita/capacitor-biometric-auth');
  const info = await BiometricAuth.checkBiometry();
  if (!info.isAvailable) return { available: false, ok: false, reason: 'not_available' };
  try {
    await BiometricAuth.authenticate({
      reason,
      cancelTitle: 'Cancel',
      allowDeviceCredential: true,
      iosFallbackTitle: 'Use passcode',
      androidTitle: 'PrimePocket',
      androidSubtitle: reason,
      androidConfirmationRequired: false,
    });
    return { available: true, ok: true };
  } catch (e) {
    return { available: true, ok: false, reason: String(e) };
  }
}

/* ---------- Native Stripe Payment Sheet ---------- */
export async function openStripePaymentSheet(paymentIntentClientSecret, customerId, ephemeralKey, publishableKey) {
  if (!isNative) {
    // Web fallback: use existing Stripe Checkout URL flow — handled by backend redirect
    return { ok: false, reason: 'use_web_checkout' };
  }
  const { Stripe } = await import('@capacitor-community/stripe');
  await Stripe.initialize({ publishableKey });
  await Stripe.createPaymentSheet({
    paymentIntentClientSecret,
    customerId,
    customerEphemeralKeySecret: ephemeralKey,
    merchantDisplayName: 'PrimePocket',
  });
  const result = await Stripe.presentPaymentSheet();
  return { ok: result.paymentResult === 'paymentSheetCompleted', result };
}

/* ---------- React hook: platform + push token ---------- */
export function useNative() {
  const [pushToken, setPushToken] = useState(null);
  const [status, setStatus] = useState({ isNative, platform });

  useEffect(() => {
    if (!isNative) return;
    initPushNotifications(setPushToken).catch(() => {});
  }, []);

  return { ...status, pushToken };
}
